import React, { useEffect, useState } from 'react'
import { App, Button, Card, Divider, Form, Input, Spin, Typography } from 'antd'
import { useIntl } from 'react-intl'
import * as Sentry from '@sentry/react'
import axios from 'axios'

import { SeoTags } from '../../components/seo-tags'
import IntlMessages from '../../util/IntlMessages'
import { getAuthTokens, IAuthNextStep, signin, confirmSigninWithCustomChallengeAnswer } from '@vacationtracker/shared/services/auth'
import * as logger from '../../services/logger'
import { track } from '../../services/analytics/analytics'

import { MicrosoftAuth } from '../../services/auth/microsoft/microsoft'
import { SlackAuth } from '../../services/auth/slack/slack'
import { GoogleAuth } from '../../services/auth/google/google'
import { getUserId as getSlackUserId } from '../../services/api/slack'
import { getUserId as getMsUserId } from '../../services/api/microsoft'
import { getUserId as getGoogleUserId } from '../../services/api/google'
import { TeamsIcon, GoogleIcon, SlackIcon } from '../../components/icons'
import { ISlackOpenidTokenResponse } from '@vacationtracker/shared/types/repository/slack-api-repository'

import { IOAuthAuthorize, IOAuthSessionDetails } from './types'
import { getClientNotAllowedMessage } from './oauth-errors'

import VtLogo from '../../assets/images/logo-purple.svg'

import './styles.less'

const { Title, Paragraph } = Typography

interface IRawCallbackPayload {
  kind: 'raw'
  clientId: string
  redirectUri: string
  scope: string
  state: string
}

interface ISessionCallbackPayload {
  kind: 'session'
  sessionId: string
}

type CallbackPayload = IRawCallbackPayload | ISessionCallbackPayload

interface IConsentContext {
  callbackPayload: CallbackPayload
  clientName: string
  scopes: string[]
  trackingId: string
  ownerType?: IOAuthSessionDetails['ownerType']
  ownerDisplayName?: string
}

// Initialize auth providers
const msAuth = new MicrosoftAuth(process.env.REACT_APP_MICROSOFT_CLIENT_ID || '')
const slackAuth = new SlackAuth(process.env.REACT_APP_SLACK_CLIENT_ID || '')
const googleAuth = new GoogleAuth(process.env.REACT_APP_GOOGLE_CLIENT_ID || '')

const OAuthAuthorize = ({ location }: IOAuthAuthorize): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const [form] = Form.useForm()

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [consentContext, setConsentContext] = useState<IConsentContext | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [clientNotAllowedMessage, setClientNotAllowedMessage] = useState<React.ReactNode | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [username, setUsername] = useState<string | null>(null)
  const [platformAuthInProgress, setPlatformAuthInProgress] = useState(false)

  const query = new URLSearchParams(location.search)

  useEffect(() => {
    let ignore = false

    const init = async () => {
      const context = await buildConsentContext(query)
      if (ignore) return

      if (!context) {
        // buildConsentContext already set the error
        setLoading(false)
        return
      }

      setConsentContext(context)

      // Check for existing session and auto-complete the consent
      try {
        const authTokens = await getAuthTokens()
        if (ignore) return
        track('OAUTH_EXISTING_SESSION_USED', { clientId: context.trackingId })
        await callOAuthCallback(authTokens.idToken, context.callbackPayload)
        return // keep spinner while redirect happens
      } catch (err) {
        if (ignore) return
        showClientNotAllowed(err)
      }

      setLoading(false)
    }

    init()

    return () => {
      ignore = true
    }
  }, [location.search])

  const buildConsentContext = async (q: URLSearchParams): Promise<IConsentContext | null> => {
    const sessionId = q.get('session')
    if (sessionId) {
      try {
        const res = await axios.get<IOAuthSessionDetails>(`${process.env.REACT_APP_API_URL}/oauth/session/${encodeURIComponent(sessionId)}`)
        return {
          callbackPayload: { kind: 'session', sessionId },
          clientName: res.data.clientName,
          scopes: res.data.scopes ?? [],
          trackingId: sessionId,
          ownerType: res.data.ownerType,
          ownerDisplayName: res.data.ownerDisplayName,
        }
      } catch (err) {
        logger.error('Failed to load OAuth session details', err)
        setError('Session not found or expired')
        return null
      }
    }

    const clientId = q.get('client_id')
    const redirectUri = q.get('redirect_uri')
    const scope = q.get('scope') || 'openid'
    const state = q.get('state') || ''

    if (!clientId || !redirectUri) {
      setError('Missing required OAuth parameters (client_id, redirect_uri)')
      return null
    }

    return {
      callbackPayload: { kind: 'raw', clientId, redirectUri, scope, state },
      clientName: getClientDisplayName(clientId),
      scopes: scope.split(' ').filter((s) => s.length > 0),
      trackingId: clientId,
    }
  }

  const showClientNotAllowed = (err: unknown): boolean => {
    const message = getClientNotAllowedMessage({ error: err, formatMessage })
    if (!message) return false
    setClientNotAllowedMessage(message)
    return true
  }

  const callOAuthCallback = async (idToken: string, payload: CallbackPayload) => {
    const body =
      payload.kind === 'session'
        ? { sessionId: payload.sessionId }
        : {
            clientId: payload.clientId,
            redirectUri: payload.redirectUri,
            scope: payload.scope,
            state: payload.state,
          }

    const callbackResponse = await axios.post(`${process.env.REACT_APP_API_URL}/oauth/callback`, body, {
      headers: {
        Authorization: idToken,
      },
    })

    if (callbackResponse.data.redirectUrl) {
      window.location.href = callbackResponse.data.redirectUrl
    }
  }

  const handleEmailCheck = async (values: { email: string }) => {
    try {
      setSubmitting(true)

      const res = await axios.post(`${process.env.REACT_APP_API_URL}/core/auth-info`, {
        email: values.email,
      })
      const response = res.data

      if (!response || !response.username) {
        notification.error({
          message: formatMessage({ id: 'error.generic' }),
          description: formatMessage({ id: 'signin.userNotFound' }),
        })
        return
      }

      // For email platform, show password input
      if (response.platform === 'email') {
        setUsername(response.username)
        setShowPassword(true)
      } else {
        // For other platforms, show appropriate message
        notification.info({
          message: formatMessage({ id: 'oauth.usePlatformLogin' }),
          description: formatMessage({ id: 'oauth.usePlatformLoginDescription' }, { platform: response.platform }),
        })
      }
    } catch (err) {
      logger.error('Error checking email', err)
      notification.error({
        message: formatMessage({ id: 'error.generic' }),
        description: formatMessage({ id: 'signin.userNotFound' }),
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleLogin = async (values: { email: string; password: string }) => {
    if (!username || !consentContext) return

    try {
      setSubmitting(true)

      const signInResponse = await signin(username, values.password)

      if ((signInResponse as IAuthNextStep).challengeName) {
        const challengeName = (signInResponse as IAuthNextStep).challengeName
        if (challengeName === 'SOFTWARE_TOKEN_MFA' || challengeName === 'SMS_MFA') {
          notification.error({
            message: formatMessage({ id: 'oauth.mfaNotSupported.title' }),
            description: formatMessage({ id: 'oauth.mfaNotSupported.description' }),
          })
          return
        }
      }

      const authTokens = await getAuthTokens()
      await callOAuthCallback(authTokens.idToken, consentContext.callbackPayload)
    } catch (err) {
      if (showClientNotAllowed(err)) return
      logger.error('Error during OAuth login', err)
      Sentry.captureException(err)

      let errorMessage = formatMessage({ id: 'error.generic' })
      if (err instanceof Error) {
        if (err.message.includes('Incorrect username or password')) {
          errorMessage = formatMessage({ id: 'signin.incorrectPassword' })
        } else if (err.message.includes('User does not exist')) {
          errorMessage = formatMessage({ id: 'signin.userNotFound' })
        }
      }

      notification.error({
        message: formatMessage({ id: 'error.auth' }),
        description: errorMessage,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const authSigninAndCallback = async (userId: string, token: string, platform: string) => {
    const signInResponse = await signin(userId)

    if ('challengeName' in signInResponse && signInResponse.challengeName === 'CUSTOM_CHALLENGE' && signInResponse.challengeParam?.question === 'token') {
      await confirmSigninWithCustomChallengeAnswer(token, { loginType: platform })
    } else if (!('challengeName' in signInResponse)) {
      // User already authenticated, no challenge needed
    } else {
      throw new Error('Unexpected auth challenge')
    }

    const authTokens = await getAuthTokens()
    if (!consentContext) throw new Error('Consent context is not initialized')
    await callOAuthCallback(authTokens.idToken, consentContext.callbackPayload)
  }

  const connectWithSlack = () => {
    setPlatformAuthInProgress(true)
    track('OAUTH_SLACK_LOGIN_STARTED', { clientId: consentContext?.trackingId })

    slackAuth
      .connect()
      .then(async (response: ISlackOpenidTokenResponse) => {
        const slackUserToken = response.authed_user.access_token
        if (!slackUserToken) {
          throw new Error('Slack user token is required')
        }

        const userData = await getSlackUserId(response.authed_user.id, slackUserToken)
        if (!userData.vacationTrackerId) {
          notification.error({
            message: formatMessage({ id: 'error.generic' }),
            description: formatMessage({ id: 'signin.userNotFound' }),
          })
          setPlatformAuthInProgress(false)
          return
        }

        // Authenticate with Cognito and call OAuth callback
        await authSigninAndCallback(userData.vacationTrackerId, slackUserToken, 'slack')
      })
      .catch((err) => {
        if (showClientNotAllowed(err)) {
          setPlatformAuthInProgress(false)
          return
        }
        // User cancelled the popup - dismiss without showing an error
        if (err instanceof Error && err.message === 'Authorization code not received') {
          setPlatformAuthInProgress(false)
          return
        }
        const errorMessage = typeof err === 'object' ? err?.response?.data || err?.message : err
        if (typeof errorMessage === 'string' && errorMessage.includes('access_denied')) {
          setPlatformAuthInProgress(false)
          return
        }
        logger.error('Slack OAuth error', err)
        notification.error({
          message: formatMessage({ id: 'error.generic' }),
          description: formatMessage({ id: 'signin.slackLoginFailed' }),
        })
        Sentry.captureException(err)
        setPlatformAuthInProgress(false)
      })
  }

  const connectWithMicrosoft = () => {
    setPlatformAuthInProgress(true)
    track('OAUTH_MICROSOFT_LOGIN_STARTED', { clientId: consentContext?.trackingId })

    msAuth
      .signin(['user.readbasic.all', 'team.readbasic.all'], false)
      .then(async ([tokens, msUser, tenantId]) => {
        const userData = await getMsUserId(tenantId, tokens.accessToken)
        if (!userData.id) {
          notification.error({
            message: formatMessage({ id: 'error.generic' }),
            description: formatMessage({ id: 'signin.userNotFound' }),
          })
          setPlatformAuthInProgress(false)
          return
        }

        // Authenticate with Cognito and call OAuth callback
        await authSigninAndCallback(userData.id, tokens.accessToken, 'microsoft')
      })
      .catch((err) => {
        if (showClientNotAllowed(err)) {
          setPlatformAuthInProgress(false)
          return
        }
        // User cancelled the popup - dismiss without showing an error
        if (err === 'access-denied-cancel') {
          setPlatformAuthInProgress(false)
          return
        }
        const errorMessage = typeof err === 'object' ? err?.response?.data || err?.message : err
        if (typeof errorMessage === 'string' && errorMessage.includes('access_denied')) {
          setPlatformAuthInProgress(false)
          return
        }
        logger.error('Microsoft OAuth error', err)
        notification.error({
          message: formatMessage({ id: 'error.generic' }),
          description: formatMessage({ id: 'signin.microsoftLoginFailed' }),
        })
        Sentry.captureException(err)
        setPlatformAuthInProgress(false)
      })
  }

  const connectWithGoogle = () => {
    setPlatformAuthInProgress(true)
    track('OAUTH_GOOGLE_LOGIN_STARTED', { clientId: consentContext?.trackingId })

    googleAuth
      .signin()
      .then(async () => {
        const googleUser = googleAuth.getSignedInUser()
        if (!googleUser.hd) {
          notification.error({
            message: formatMessage({ id: 'connect.google.notWorkspaceUserTitle' }),
            description: formatMessage({ id: 'connect.google.notWorkspaceUserDescription' }),
          })
          setPlatformAuthInProgress(false)
          return
        }

        const userData = await getGoogleUserId(googleAuth.getIdToken())
        if (!userData.vacationTrackerId) {
          notification.error({
            message: formatMessage({ id: 'error.generic' }),
            description: formatMessage({ id: 'signin.userNotFound' }),
          })
          setPlatformAuthInProgress(false)
          return
        }

        // Authenticate with Cognito and call OAuth callback
        await authSigninAndCallback(userData.vacationTrackerId, googleAuth.getAccessToken(), 'google')
      })
      .catch((err) => {
        if (showClientNotAllowed(err)) {
          setPlatformAuthInProgress(false)
          return
        }
        // User cancelled the popup - dismiss without showing an error
        if (err && typeof err === 'object' && err.error === 'access_denied') {
          setPlatformAuthInProgress(false)
          return
        }
        const errorMessage = typeof err === 'object' ? err?.response?.data || err?.message : err
        if (typeof errorMessage === 'string' && errorMessage.includes('access_denied')) {
          setPlatformAuthInProgress(false)
          return
        }
        logger.error('Google OAuth error', err)
        notification.error({
          message: formatMessage({ id: 'error.generic' }),
          description: formatMessage({ id: 'signin.googleLoginFailed' }),
        })
        Sentry.captureException(err)
        setPlatformAuthInProgress(false)
      })
  }

  const getClientDisplayName = (clientId: string): string => {
    const clientNames: Record<string, string> = {
      'zapier-vt-client': 'Zapier',
    }
    return clientNames[clientId] || clientId
  }

  const getScopeDescription = (scope: string): string => {
    const scopeDescriptions: Record<string, string> = {
      openid: formatMessage({ id: 'oauth.scope.openid' }),
      email: formatMessage({ id: 'oauth.scope.email' }),
      profile: formatMessage({ id: 'oauth.scope.profile' }),
      'zapier:webhooks': formatMessage({ id: 'oauth.scope.zapierWebhooks' }),
      'mcp:workspace:read': formatMessage({ id: 'oauth.scope.mcpWorkspaceRead' }),
      'mcp:users:read': formatMessage({ id: 'oauth.scope.mcpUsersRead' }),
      'mcp:leaves:read': formatMessage({ id: 'oauth.scope.mcpLeavesRead' }),
      'mcp:workspace:write': formatMessage({ id: 'oauth.scope.mcpWorkspaceWrite' }),
      'mcp:users:write': formatMessage({ id: 'oauth.scope.mcpUsersWrite' }),
      'mcp:leaves:write': formatMessage({ id: 'oauth.scope.mcpLeavesWrite' }),
    }
    return scopeDescriptions[scope] || scope
  }

  if (loading || platformAuthInProgress) {
    return (
      <div className="oauth-authorize-container">
        <Spin size="large" />
      </div>
    )
  }

  if (clientNotAllowedMessage) {
    return (
      <div className="oauth-authorize-container">
        <Card className="oauth-authorize-card">
          <Title level={3} className="oauth-heading">
            <IntlMessages id="oauth.authorize.clientNotAllowed.title" />
          </Title>
          <Paragraph>{clientNotAllowedMessage}</Paragraph>
          <Paragraph>
            <IntlMessages id="oauth.authorize.clientNotAllowed.hint" />
          </Paragraph>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="oauth-authorize-container">
        <Card className="oauth-authorize-card">
          <Title level={3} className="oauth-heading">
            <IntlMessages id="error.generic" />
          </Title>
          <Paragraph>{error}</Paragraph>
        </Card>
      </div>
    )
  }

  const scopes = consentContext?.scopes || []

  return (
    <div className="oauth-authorize-container">
      <SeoTags title="oauth.authorize.title" description="oauth.authorize.description" />

      <Card className="oauth-authorize-card">
        <div className="oauth-logo">
          <VtLogo />
        </div>

        <Title level={3} className="oauth-heading">
          <IntlMessages id="signin.goodToSeeYouAgain" />
        </Title>

        <Paragraph className="oauth-subheading">
          <IntlMessages id="oauth.authorize.signInTo" /> <strong>{consentContext?.clientName || ''}</strong>
        </Paragraph>

        {consentContext?.ownerType === 'company' && consentContext.ownerDisplayName && (
          <Paragraph className="oauth-subheading">
            <IntlMessages id="oauth.authorize.createdByCompany" values={{ companyName: consentContext.ownerDisplayName }} />
          </Paragraph>
        )}
        {consentContext?.ownerType === 'personal' && consentContext.ownerDisplayName && (
          <Paragraph className="oauth-subheading">
            <IntlMessages id="oauth.authorize.personalClientOf" values={{ name: consentContext.ownerDisplayName }} />
          </Paragraph>
        )}

        {/* Step 2: Show permissions after email/password step */}
        {showPassword && scopes.length > 0 && (
          <div className="oauth-scopes">
            <div className="oauth-scopes-title">
              <IntlMessages id="oauth.authorize.permissions" />
            </div>
            <ul>
              {scopes.map((scope) => (
                <li key={scope}>{getScopeDescription(scope)}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Step 1: Platform buttons (only show when not in password step) */}
        {!showPassword && (
          <>
            <div className="oauth-platform-buttons">
              <Button size="large" block onClick={connectWithMicrosoft}>
                <TeamsIcon />
                <IntlMessages id="oauth.authorize.signInWithMicrosoft" />
              </Button>

              <Button size="large" block onClick={connectWithGoogle}>
                <GoogleIcon />
                <IntlMessages id="oauth.authorize.signInWithGoogle" />
              </Button>

              <Button size="large" block onClick={connectWithSlack}>
                <SlackIcon />
                <IntlMessages id="oauth.authorize.signInWithSlack" />
              </Button>
            </div>

            <Divider className="oauth-divider">
              <IntlMessages id="app.or" />
            </Divider>
          </>
        )}

        <Form form={form} layout="vertical" onFinish={showPassword ? handleLogin : handleEmailCheck}>
          <Form.Item
            name="email"
            label={!showPassword ? <IntlMessages id="app.email" /> : undefined}
            rules={[
              { required: true, message: formatMessage({ id: 'form.inputRequired' }) },
              { type: 'email', message: formatMessage({ id: 'form.validEmailRequired' }) },
            ]}
          >
            <Input placeholder={formatMessage({ id: 'app.enterWorkEmail' })} size="large" disabled={showPassword} />
          </Form.Item>

          {showPassword && (
            <Form.Item name="password" label={<IntlMessages id="app.password" />} rules={[{ required: true, message: formatMessage({ id: 'form.inputRequired' }) }]}>
              <Input.Password placeholder={formatMessage({ id: 'app.passwordPlaceholder' })} size="large" autoFocus />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
              {showPassword ? <IntlMessages id="oauth.authorize.authorizeButton" /> : <IntlMessages id="app.continue" />}
            </Button>
          </Form.Item>

          {showPassword && (
            <div className="oauth-change-email">
              <Button
                type="link"
                onClick={() => {
                  setShowPassword(false)
                  setUsername(null)
                  form.setFieldsValue({ password: '' })
                }}
              >
                <IntlMessages id="oauth.authorize.changeEmail" />
              </Button>
            </div>
          )}
        </Form>

        <Paragraph className="oauth-security-note">
          <IntlMessages id="oauth.authorize.securityNote" />
        </Paragraph>
      </Card>
    </div>
  )
}

export default OAuthAuthorize
